// The heat budget - a ship in vacuum sheds heat only by radiating, so rejection
// is proportional to deployed panel area and a stowed hull is on a clock.
//
// Units are abstract: the ceiling is 100 and everything else is stated against
// it. Rejection scales with the stored heat as well as with the deployed area,
// which is what gives a deployed hull an EQUILIBRIUM fraction rather than a
// rate that only ever wins or only ever loses.
//
// Pure: no three, no DOM, no timers. The sim tick steps it - the captain
// registry for an NPC, src/main.ts for the player - and nothing steps it from
// a render callback.

/** Stored heat at the ceiling. Every fraction below is against this. */
export const HEAT_CEILING = 100;

/** Units per second the reactor puts in whatever the ship is doing. */
export const HEAT_REACTOR_FLOOR = 0.75;
/** Units per second added at full throttle, on top of the floor. */
export const HEAT_THROTTLE_GAIN = 1.55;
/** Units per second a rigged hull sheds at the ceiling with the panels stowed. */
export const HEAT_HULL_REJECT = 1.2;
/**
 * Units per second a hull with no radiator spars sheds at the ceiling: a hull
 * carrying no panels is a switch in hullReject rather than a term scaled to
 * her own size, so every sparless class sheds this same 4.6 - above the Pup's
 * fully-deployed 4.2 (HEAT_HULL_REJECT + HEAT_PANEL_REJECT * 2 * 2), and that
 * crossover is stated in docs/VERIFICATION.md's heat-budget row. Sized off the
 * ladder itself - at full throttle she holds half the ceiling, and she is
 * under the warning rung at every load, so no rung asks her for a posture she
 * has not got: 24.7 percent at quarter throttle, 31.5 at the 0.45 the cap
 * holds, 50.0 at full.
 */
export const HEAT_SPARLESS_REJECT = 4.6;
/** Units per second one panel sheds at the ceiling, fully deployed. */
export const HEAT_PANEL_REJECT = 0.75;
/** Stored heat a ship is built with, as a fraction of the ceiling. */
export const HEAT_START_FRAC = 0.18;
/** Seconds the panels take to travel end to end (scene/custos.ts POSTURE). */
export const HEAT_POSTURE_TRAVEL_S = 1.6;

/** Discrete heat one discharge dumps into the hull, by weapon. */
export const HEAT_WEAPON_SPIKE: Readonly<Record<string, number>> = {
  plasma: 7,
  phaser: 2.5,
  pulse: 3.5,
  missile: 5,
};

export interface HeatRungSpec {
  /** Fraction of the ceiling this rung bites at. */
  frac: number;
  /** What the rung does, in the words the comms log uses. */
  label: string;
}

/** The ladder. Index 0 is rung 1; rung 0 means the ship is clear of all of them. */
export const HEAT_RUNGS: readonly HeatRungSpec[] = [
  { frac: 0.55, label: 'THERMAL WARNING - radiators wanted' },
  { frac: 0.75, label: 'THERMAL THROTTLE CAP - drive held down' },
  { frac: 0.90, label: 'WEAPONS THERMAL LOCKOUT - mounts cold' },
  { frac: 1.00, label: 'THERMAL DAMAGE - the hull is cooking' },
];

/** Throttle ceiling, as a fraction of the class maximum, from rung 2 up. */
export const HEAT_THROTTLE_CAP = 0.45;
/** Stored fraction a warped drive, and a trip's burn, is eased to settle at:
 *  short of rung 2, so the ease never lands the hull on the ladder's own clamp. */
export const HEAT_WARP_EASE_FRAC = 0.72;
/** Stored fraction a drive held on a trip settles at: on rung 2, so the hold
 *  never lets go and grabs again, and short of the lockout and the ceiling. */
export const HEAT_TRIP_HOLD_FRAC = 0.8;
/** Seconds between module damage steps while the ship sits on rung 4, or past
 *  the red line while the player's override is on. */
export const HEAT_DAMAGE_INTERVAL_S = 6;
/** Rung a captain abandons a stow at, and the rung it will stow again below. */
export const HEAT_STOW_ABORT_FRAC = 0.75;
export const HEAT_STOW_RESUME_FRAC = 0.45;
/** Rejection at or under this is no rejection: the closed form divides by it. */
export const HEAT_REJECT_EPSILON = 1e-9;

/**
 * Per-ship thermal state. `inRate` / `outRate` / `netRate` are the last step's
 * own numbers, kept so a readout does not have to re-derive them.
 */
export interface HeatState {
  /** Stored heat, 0 to HEAT_CEILING. */
  heat: number;
  /** Rejection capacity comes from this and nothing else. */
  panelsPerSide: number;
  /** Highest rung currently bitten, 0 through HEAT_RUNGS.length. */
  rung: number;
  /** Seconds of dwell at the top rung since the last module damage step. */
  sinceDamageS: number;
  inRate: number;
  outRate: number;
  netRate: number;
}

export function createHeatState(panelsPerSide: number): HeatState {
  return {
    heat: HEAT_CEILING * HEAT_START_FRAC,
    panelsPerSide: Math.max(0, panelsPerSide),
    rung: 0,
    sinceDamageS: 0,
    inRate: 0,
    outRate: 0,
    netRate: 0,
  };
}

/**
 * Put stored heat at a given figure. The rung comes back off that figure
 * rather than off zero, so restoring a hot hull does not re-announce every
 * rung it had already crossed; the rates are last step's own and a step has
 * not run yet.
 */
export function setHeat(hs: HeatState, units: number): void {
  hs.heat = units;
  hs.rung = rungFor(heatFrac(hs));
  hs.sinceDamageS = 0;
  hs.inRate = 0;
  hs.outRate = 0;
  hs.netRate = 0;
}

/** Stored heat a cold hull opens on: a scenario restart and the player's respawn. */
export const HEAT_INITIAL = HEAT_CEILING * HEAT_START_FRAC;

/** Back to a cold hull - the player's respawn and a scenario reload. */
export function resetHeat(hs: HeatState): void {
  setHeat(hs, HEAT_INITIAL);
}

/** Stored heat against the ceiling, 0 to 1 and beyond only transiently. */
export function heatFrac(hs: HeatState): number {
  return hs.heat / HEAT_CEILING;
}

/**
 * The control: pin the panel capacity to a constant so rejection no longer
 * tracks either the panel count or the deploy fraction. `null` is the shipped
 * path. Set by tools/preview/heat-budget-check.mjs --inject capacity-constant.
 */
let capacityPinned: number | null = null;
export function setCapacityPinned(units: number | null): void {
  capacityPinned = units;
}

/**
 * The control: put the old per-frame explicit-Euler step back, unconditionally
 * stable only while `dt < 2 * tau`. Set by
 * tools/preview/heat-budget-check.mjs --inject euler-heat, to show what time
 * warp did to the shipped step before this fix.
 */
let eulerHeatInjected = false;
export function setEulerHeat(on: boolean): void {
  eulerHeatInjected = on;
}

/**
 * The control: put the rigged hull's flat constant on every hull, so a hull
 * with no radiator spars sheds 1.2 units a second whatever the load and cooks
 * under the ladder's own throttle cap. Set by
 * tools/preview/heat-budget-check.mjs --inject zero-panel-cooks.
 */
let sparlessRejectFlat = false;
export function setSparlessRejectFlat(on: boolean): void {
  sparlessRejectFlat = on;
}

/**
 * The state: the bare-hull term, which a radiator rig shot away takes to zero
 * and which a hull that never had one carries in its skin. `null` is the
 * shipped path; 0 is a hull that rejects NOTHING, the linear-climb case in
 * stepHeat. Stated by tools/preview/heat-budget-check.mjs reading 10.
 */
let hullRejectPinned: number | null = null;
export function setHullRejectPinned(units: number | null): void {
  hullRejectPinned = units;
}

/**
 * The control: put the bare closed form back, dividing by the rejection rate
 * with no zero case, so a hull that rejects nothing reads NaN instead of the
 * climb its in rate owes. Set by
 * tools/preview/heat-budget-check.mjs --inject reject-divides.
 */
let rejectDividesInjected = false;
export function setRejectDivides(on: boolean): void {
  rejectDividesInjected = on;
}

/**
 * The control: leave the rung where it was when a spike lands, so a spike that
 * crosses a rung is announced a step late and a relaxation inside the same
 * step can hide the peak rung entirely. Set by
 * tools/preview/heat-budget-check.mjs --inject spike-skips-rung.
 */
let spikeSkipsRungInjected = false;
export function setSpikeSkipsRung(on: boolean): void {
  spikeSkipsRungInjected = on;
}

/**
 * The control: charge the heat model the bar the player set rather than the
 * throttle the drive was given, which is what the fight branch did while
 * cruise charged the capped value. Set by
 * tools/preview/heat-budget-check.mjs --inject cap-cruise-only.
 */
let barChargedInjected = false;
export function setBarCharged(on: boolean): void {
  barChargedInjected = on;
}

/**
 * The control: drop the ladder's clock hold, so a hull that cannot shed even an
 * idle drive keeps the frame the warp rate bought and runs the whole ladder - the ceiling
 * dwell and the module damage with it - inside one warped frame. Set by
 * tools/preview/heat-budget-check.mjs --inject heat-frees-the-clock.
 */
let heatFreesClockInjected = false;
export function setHeatFreesClock(on: boolean): void {
  heatFreesClockInjected = on;
}

/**
 * The control: put back the split a trip's departure ran long on - the burn
 * planned on the load that settles ON the clamp rung, and flown on the ladder
 * and the warp ease instead of at that share. Set by
 * tools/preview/transit-check.mjs --inject departure-split.
 */
let departureSplitInjected = false;
export function setDepartureSplit(on: boolean): void {
  departureSplitInjected = on;
}
export function departureSplit(): boolean {
  return departureSplitInjected;
}

/** Units per second the panels shed at the ceiling when fully deployed. */
export function heatCapacity(panelsPerSide: number): number {
  return HEAT_PANEL_REJECT * 2 * Math.max(0, panelsPerSide);
}

/**
 * Units per second the whole ship sheds AT THE CEILING in this posture. A hull
 * with no radiator spars sheds through her whole skin rather than through a
 * wing, and either term can be pinned - the hull term to zero is a hull that
 * rejects nothing at all, which stepHeat reads as pure heating.
 */
export function rejectionRate(panelsPerSide: number, deployFraction: number): number {
  const f = clamp01(deployFraction);
  const panels = capacityPinned === null ? heatCapacity(panelsPerSide) * f : capacityPinned;
  return hullReject(panelsPerSide) + panels;
}

/** The bare-hull half of the rejection rate, by hull and by pin. */
function hullReject(panelsPerSide: number): number {
  if (hullRejectPinned !== null) return hullRejectPinned;
  return panelsPerSide > 0 || sparlessRejectFlat ? HEAT_HULL_REJECT : HEAT_SPARLESS_REJECT;
}

/** Units per second going in at this throttle. */
export function heatInRate(throttle: number): number {
  return HEAT_REACTOR_FLOOR + HEAT_THROTTLE_GAIN * clamp01(Math.abs(throttle));
}

/**
 * Commanded throttle after the ladder's own cap, in the caller's own units.
 * From rung 2 up the drive is held to HEAT_THROTTLE_CAP of the class maximum
 * whatever the bar reads, and that one value is what the ship flies, what the
 * heat model is charged and what the bar shows. `max` is the caller's full
 * scale: 1 for cruise's [-1, 1] bar, the class's maxSpeed for the fight's.
 * `pastRedLine` is the player's override, which lifts the hold.
 */
export function thermalThrottle(commanded: number, rung: number, max: number, pastRedLine = false): number {
  if (rung < 2 || pastRedLine) return commanded;
  const cap = HEAT_THROTTLE_CAP * Math.abs(max);
  return commanded > cap ? cap : commanded < -cap ? -cap : commanded;
}

/**
 * Share of full throttle a trip's burn is planned on and flown at in this
 * posture: the whole drive where full throttle settles short of the clamp
 * rung, otherwise the load that settles where the warp ease does, and never
 * under the trip hold (`tripHoldThrottle`), which a stowed hull settles on the
 * clamp rung at. From any heat it carries the hull toward that settling point,
 * so the burn needs no ladder hold on top of it.
 */
export function sustainedThrottle(panelsPerSide: number, deployFraction: number): number {
  const settleAt = departureSplitInjected ? HEAT_RUNGS[1]!.frac : HEAT_WARP_EASE_FRAC;
  return Math.max(tripHoldThrottle(panelsPerSide, deployFraction),
    settlingThrottle(panelsPerSide, deployFraction, settleAt));
}

/**
 * The drive the ladder holds a trip to from rung 2 up: HEAT_THROTTLE_CAP, or
 * less where the panels as they stand cannot shed that, so a stowed hull
 * settles at HEAT_TRIP_HOLD_FRAC instead of cooking. A fight keeps the flat cap.
 */
export function tripHoldThrottle(panelsPerSide: number, deployFraction: number): number {
  return Math.min(HEAT_THROTTLE_CAP, settlingThrottle(panelsPerSide, deployFraction, HEAT_TRIP_HOLD_FRAC));
}

/**
 * The drive a warped frame is eased to: the load that settles at
 * HEAT_WARP_EASE_FRAC in this posture, 1 where full throttle settles under it.
 * A warped frame is minutes to a day of sim time, so the ship flies what its
 * radiators shed rather than handing the ladder a whole frame to climb in.
 */
export function warpEaseThrottle(panelsPerSide: number, deployFraction: number): number {
  return settlingThrottle(panelsPerSide, deployFraction, HEAT_WARP_EASE_FRAC);
}

/** The load, 0 to 1, whose equilibrium sits at `frac` of the ceiling. */
function settlingThrottle(panelsPerSide: number, deployFraction: number, frac: number): number {
  const reject = rejectionRate(panelsPerSide, deployFraction);
  return clamp01((reject * frac - HEAT_REACTOR_FLOOR) / HEAT_THROTTLE_GAIN);
}

/**
 * The throttle the heat model charges for, as a fraction of the class maximum:
 * the value the drive was actually given, so the heat falls with the drive the
 * cap held down. `barFrac` is what the player asked for and is passed for the
 * control that charges it instead - a fight read the bar while cruise read the
 * capped value, and a hull that flies at 45 percent must not cook at 100.
 */
export function heatThrottleCharged(barFrac: number, appliedFrac: number): number {
  return barChargedInjected ? barFrac : appliedFrac;
}

/** Which rung a stored fraction sits on. 0 means clear of the ladder. */
export function rungFor(frac: number): number {
  let r = 0;
  for (let i = 0; i < HEAT_RUNGS.length; i++) {
    if (frac >= HEAT_RUNGS[i]!.frac) r = i + 1;
  }
  return r;
}

/** Stored heat at the rung the clock hold bites at, the warning rung's own. */
const HEAT_HOLD_AT = HEAT_CEILING * HEAT_RUNGS[0]!.frac;

/**
 * Whether the ladder is holding the clock at real time: on the warning rung or
 * above it, gaining heat, and in a posture where even an idle drive would carry
 * the hull to the ceiling, so no ease of the throttle can keep a warped frame
 * off the damage rung. Any hull that sheds more than the reactor floor is eased
 * under warp instead (`warpEaseThrottle`) and keeps the clock. Read fresh off
 * the state and posture a step is about to use.
 */
export function heatHoldsClock(hs: HeatState, input: HeatStepInput): boolean {
  if (heatFreesClockInjected) return false;
  return hs.heat >= HEAT_HOLD_AT && idleCooks(hs, input) && heatNetRate(hs, input) > 0;
}

/** An idle drive's equilibrium sits at or past the ceiling in this posture. */
function idleCooks(hs: HeatState, input: HeatStepInput): boolean {
  return rejectionRate(hs.panelsPerSide, input.deployFraction) <= HEAT_REACTOR_FLOOR;
}

/** Stored heat the ladder holds the drive down at: where a stowed hull's
 *  allowance ends, and the red line the player's override pushes past. */
export const HEAT_RED_LINE = HEAT_CEILING * HEAT_RUNGS[1]!.frac;

/**
 * Sim seconds a hull at this load and posture runs before the ladder holds its
 * drive down: 0 once it is there, Infinity where it settles short of it.
 * Weapon spikes are not in it; each one spends some of it at once.
 */
export function heatAllowanceS(hs: HeatState, input: HeatStepInput): number {
  if (hs.heat >= HEAT_RED_LINE) return 0;
  const inRate = heatInRate(input.throttle);
  const reject = rejectionRate(hs.panelsPerSide, input.deployFraction);
  if (reject <= HEAT_REJECT_EPSILON) return (HEAT_RED_LINE - hs.heat) / inRate;
  return timeToFrac(hs.heat, (HEAT_CEILING * inRate) / reject, HEAT_CEILING / reject, HEAT_RED_LINE);
}

/** Heat left before the drive is held down, against that rung's own heat: 1 empty, 0 spent. */
export function heatHeadroom(hs: HeatState): number {
  return clamp01((HEAT_RED_LINE - hs.heat) / HEAT_RED_LINE);
}

/** What a step from `hs` at this throttle and posture gains less what it sheds. */
function heatNetRate(hs: HeatState, input: HeatStepInput): number {
  const reject = rejectionRate(hs.panelsPerSide, input.deployFraction);
  return heatInRate(input.throttle) - reject * heatFrac(hs);
}

/**
 * The sim seconds of a step of `dt` the ladder lets the player's rate buy, and
 * the point a warp frame is split on: `dt` whole where the step would not reach
 * the warning rung or the hull sheds more than an idle drive makes (the ease
 * keeps that one), 0 where the hull is already on it and rising, and the
 * step's own crossing time where it reaches it partway. The caller integrates
 * those seconds at the picked rate and the rest of the real frame at 1x.
 */
export function heatHoldWarpedS(hs: HeatState, dt: number, input: HeatStepInput): number {
  if (heatFreesClockInjected || dt <= 0 || !idleCooks(hs, input)) return dt;
  if (hs.heat >= HEAT_HOLD_AT) return heatHoldsClock(hs, input) ? 0 : dt;
  const inRate = heatInRate(input.throttle);
  const reject = rejectionRate(hs.panelsPerSide, input.deployFraction);
  if (heatNetRate(hs, input) <= 0) return dt;
  const t = reject <= HEAT_REJECT_EPSILON && !rejectDividesInjected
    ? (HEAT_HOLD_AT - hs.heat) / inRate
    : timeToFrac(hs.heat, (HEAT_CEILING * inRate) / reject, HEAT_CEILING / reject, HEAT_HOLD_AT);
  return t <= dt ? t : dt;
}

/**
 * A discharge, a hit, anything that dumps heat in one go. The rung moves with
 * the heat in the same call - a step that started from the rung before the
 * spike would compare it against a state the ship is no longer in and could
 * report the relaxation while missing the peak - so the spike returns the
 * rungs it crosses itself, at 0 s into whatever frame it landed in.
 */
export function spikeHeat(hs: HeatState, units: number): HeatCrossing[] {
  const before = hs.rung;
  hs.heat = Math.min(HEAT_CEILING, hs.heat + units);
  if (!spikeSkipsRungInjected) hs.rung = rungFor(heatFrac(hs));
  const crossings: HeatCrossing[] = [];
  for (let r = before + 1; r <= hs.rung; r++) crossings.push({ rung: r, atS: 0 });
  return crossings;
}

export interface HeatStepInput {
  /** Throttle as a fraction of the class maximum, 0 to 1. */
  throttle: number;
  /** Where the panels actually are this tick, 0 stowed to 1 deployed. */
  deployFraction: number;
  /** The player's override is on: every HEAT_DAMAGE_INTERVAL_S spent at or
   *  over HEAT_RED_LINE costs a module, not only the ceiling's. */
  pastRedLine?: boolean;
}

export interface HeatCrossing {
  /** The rung landed on: 0 means clear of the ladder again. */
  rung: number;
  /** Seconds into THIS step, from the step's own start, that the rung was reached. */
  atS: number;
}

export interface HeatStepResult {
  /** The rung after the step. */
  rung: number;
  /** The rung before it. */
  previousRung: number;
  /**
   * Every rung boundary this step passed through, in the order and direction
   * crossed - a big warped step can cross several at once, each reported once.
   * Empty when `rung === previousRung`.
   */
  crossings: HeatCrossing[];
  /** Module-damage steps owed this step: dwell at the ceiling, or over the red
   *  line under the override, over HEAT_DAMAGE_INTERVAL_S. */
  damageSteps: number;
}

/**
 * Seconds within a step, from a state at `h0` relaxing toward `eq` on time
 * constant `tau`, before it reaches `target`. `target` must lie strictly
 * between `h0` and `eq` (the exponential only ever approaches `eq`, never
 * crosses or overshoots it) or the answer is Infinity - never reached.
 */
function timeToFrac(h0: number, eq: number, tau: number, target: number): number {
  if (h0 === target) return 0;
  if (h0 === eq) return Infinity;
  const ratio = (target - eq) / (h0 - eq);
  if (ratio <= 0 || ratio > 1) return Infinity;
  return -tau * Math.log(ratio);
}

/**
 * One sim tick of the budget, exact over the whole of `dt` however large -
 * the ODE is linear per step (throttle and deployFraction are each held
 * constant for the step by the caller, so `in` and the rejection rate are
 * constants), so it is solved in closed form rather than marched forward:
 * `h(t) = eq + (h0 - eq) * exp(-t / tau)`, `eq = in * HEAT_CEILING / reject`,
 * `tau = HEAT_CEILING / reject`. Unconditionally stable at any `dt`, unlike
 * the explicit-Euler step `--inject euler-heat` restores, which only holds
 * bounded while `dt < 2 * tau` - a warped frame is routinely 10 to 1000
 * times that. A rejection rate of zero is the one case the form cannot carry
 * (it divides by it) and is stepped as the linear climb instead. Mutates `hs`.
 */
export function stepHeat(hs: HeatState, dt: number, input: HeatStepInput): HeatStepResult {
  const previousRung = hs.rung;
  if (dt <= 0) return { rung: hs.rung, previousRung, crossings: [], damageSteps: 0 };

  const inRate = heatInRate(input.throttle);
  const reject = rejectionRate(hs.panelsPerSide, input.deployFraction);
  const h0 = hs.heat;
  hs.inRate = inRate;
  hs.outRate = reject * heatFrac(hs);
  hs.netRate = inRate - hs.outRate;

  let hFinal: number;
  let ceilingS = 0;
  const crossings: HeatCrossing[] = [];

  const noReject = reject <= HEAT_REJECT_EPSILON && !rejectDividesInjected;
  const eq = noReject ? Infinity : (HEAT_CEILING * inRate) / reject;
  const tau = noReject ? Infinity : HEAT_CEILING / reject;
  const heatAt = (t: number): number =>
    noReject ? h0 + inRate * t : eq + (h0 - eq) * Math.exp(-t / tau);
  const timeToHeat = (target: number): number =>
    noReject ? (inRate > 0 ? (target - h0) / inRate : Infinity) : timeToFrac(h0, eq, tau, target);

  if (eulerHeatInjected) {
    hFinal = h0 + hs.netRate * dt;
  } else if (eq >= HEAT_CEILING) {
    const tCeil = h0 >= HEAT_CEILING ? 0 : timeToHeat(HEAT_CEILING);
    if (tCeil <= dt) {
      ceilingS = dt - Math.max(0, tCeil);
      hFinal = HEAT_CEILING;
    } else {
      hFinal = heatAt(dt);
    }
  } else {
    hFinal = heatAt(dt);
  }

  if (!eulerHeatInjected) {
    // Rungs crossed, in order: the exact trajectory is monotonic this step
    // (relaxing toward one eq, never overshooting), so every threshold
    // strictly between the start and end fraction was passed through once.
    const provisionalRung = rungFor(clamp01(hFinal / HEAT_CEILING));
    if (provisionalRung > previousRung) {
      for (let r = previousRung + 1; r <= provisionalRung; r++) {
        const atS = Math.min(dt, timeToHeat(HEAT_CEILING * HEAT_RUNGS[r - 1]!.frac));
        crossings.push({ rung: r, atS });
      }
    } else if (provisionalRung < previousRung) {
      for (let r = previousRung - 1; r >= provisionalRung; r--) {
        const atS = Math.min(dt, timeToHeat(HEAT_CEILING * HEAT_RUNGS[r]!.frac));
        crossings.push({ rung: r, atS });
      }
    }
  }

  hs.heat = Math.max(0, Math.min(HEAT_CEILING, hFinal));
  hs.rung = rungFor(heatFrac(hs));

  if (eulerHeatInjected && hs.rung !== previousRung) crossings.push({ rung: hs.rung, atS: dt });

  let damageSteps = 0;
  const pastLine = input.pastRedLine === true;
  if (pastLine ? hs.heat >= HEAT_RED_LINE : hs.rung >= HEAT_RUNGS.length) {
    if (eulerHeatInjected) {
      hs.sinceDamageS += dt;
      if (hs.sinceDamageS >= HEAT_DAMAGE_INTERVAL_S) {
        hs.sinceDamageS = 0;
        damageSteps = 1;
      }
    } else {
      // The trajectory is monotonic within a step, so a step ending over the
      // line spent all of it there unless it started under it.
      hs.sinceDamageS += !pastLine ? ceilingS : h0 >= HEAT_RED_LINE ? dt : Math.max(0, dt - timeToHeat(HEAT_RED_LINE));
      while (hs.sinceDamageS >= HEAT_DAMAGE_INTERVAL_S) {
        hs.sinceDamageS -= HEAT_DAMAGE_INTERVAL_S;
        damageSteps++;
      }
    }
  } else {
    hs.sinceDamageS = 0;
  }
  return { rung: hs.rung, previousRung, crossings, damageSteps };
}

/** Advance a posture toward its target at the panels' own travel rate. */
export function stepDeploy(deployFraction: number, deployTarget: number, dt: number): number {
  const target = clamp01(deployTarget);
  const step = dt / HEAT_POSTURE_TRAVEL_S;
  if (deployFraction < target) return Math.min(target, deployFraction + step);
  if (deployFraction > target) return Math.max(target, deployFraction - step);
  return deployFraction;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
